import CallToActionHeader from "components/CallToAction/CallToActionHeader";
import CallToFaqSection from "components/CallToAction/CallToFaqSection";
import HowItWorkCallToAction from "components/CallToAction/HowItWorkCallToAction";
import { RESERVATION_FAQ, RESERVATION_HOW_IT_WORKS } from "data/constants";
import AboutUsPage from "elements/Pages";
import AdvanceTabPage from "elements/advance-tab";
import { FC } from "react";
import { Helmet } from "react-helmet";
import { IoIosSend } from "react-icons/io";
import { FaRocket, FaUsers, FaHeart } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import presta from "../../images/commentcamarche/prestataires_evenementiels.png";
import "../../styles/css/next-styles.css";
import Banner from "./Banner";
import BenefitsSection from "./BenefitsSection";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";

export interface SectionHowItWorkProps {
  className?: string;
}

const HowItWorks: FC<SectionHowItWorkProps> = ({ className = "pt-6 pb-6" }) => {
  const navigate = useNavigate();
  return (
    <>
      <Helmet>
        <title>
          Réservez un espace événementiel à l'heure | Trouvez un prestataire événementiel facilement – Comment ça
          marche ?{" "}
        </title>
        <meta
          name="description"
          content="Réserver à l'heure un espace événementiel pour votre événement. trouver un prestataire événementiels en toute simplicité."
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        {/* ======== BG GLASS ======== */}
        <BgGlassmorphism />

        {/* Hero Section */}
        <div className="relative pt-16 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-pink-50 to-purple-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900"></div>
          <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-[#4663ac]/30 to-pink-300/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-pink-200/20 to-[#4663ac]/20 rounded-full blur-3xl"></div>

          <div className="container relative">
            <div className="text-center max-w-4xl mx-auto animate-fade-in-up">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-50 to-pink-50 rounded-full text-sm font-medium text-[#4663ac] mb-6">
                <span className="w-2 h-2 bg-[#4663ac] rounded-full mr-2 animate-pulse"></span>
                Comment ça marche
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-[#4663ac] via-pink-500 to-purple-600 bg-clip-text text-transparent mb-6">
                Créez, partagez, célébrez
              </h1>
              <p className="text-xl md:text-2xl text-neutral-600 dark:text-neutral-400 mb-8 leading-relaxed">
                Nous trouvons le lieu parfait pour vos événements
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-12">
                <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
                  <FaUsers className="w-5 h-5 text-[#4663ac] mr-2" />
                  <span className="text-neutral-700 dark:text-neutral-300 font-medium">Simple et efficace</span>
                </div>
                <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
                  <FaHeart className="w-5 h-5 text-pink-500 mr-2" />
                  <span className="text-neutral-700 dark:text-neutral-300 font-medium">Lieux vérifiés</span>
                </div>
                <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
                  <FaRocket className="w-5 h-5 text-purple-500 mr-2" />
                  <span className="text-neutral-700 dark:text-neutral-300 font-medium">Réservation rapide</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About Us Section */}
        <div className="animate-fade-in-up animation-delay-200">
          <AboutUsPage />
        </div>

        {/* Advance Tab Section */}
        <div className="animate-fade-in-up animation-delay-400">
          <AdvanceTabPage />
        </div>

        {/* How It Works Section */}
        <div className="mt-16 animate-fade-in-up animation-delay-600">
          <HowItWorkCallToAction
            title={RESERVATION_HOW_IT_WORKS.title}
            stepCircleColor="bg-gradient-to-r from-[#4663ac] to-pink-500"
            steps={RESERVATION_HOW_IT_WORKS.steps}
            titleHighlight=""
            bg="bg-white dark:bg-neutral-800"
          />
        </div>

        {/* Benefits Section */}
        <div className="animate-fade-in-up animation-delay-800">
          <BenefitsSection />
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-white dark:bg-neutral-800 animate-fade-in-up animation-delay-1000">
          <div className="container mx-auto px-4">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#4663ac] via-pink-500 to-purple-500 p-12 text-center">
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              <div className="absolute -top-6 -left-6 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>

              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                  Réservez votre espace maintenant
                </h2>
                <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                  Découvrez nos lieux disponibles et trouvez l'espace parfait pour votre événement
                </p>
                <Link to={"/espace-evenementiel"}>
                  <button className="inline-flex items-center justify-center px-8 py-4 bg-white !text-[#4663ac] font-semibold rounded-full hover:bg-neutral-100 hover:!text-[#4663ac] transform hover:scale-105 transition-all duration-300 space-x-2">
                    <IoIosSend className="w-6 h-6" />
                    <span>Découvrez nos lieux disponibles</span>
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Prestataire Section */}
        <div className="animate-fade-in-up animation-delay-1200">
          <CallToActionHeader
            bg="bg-white dark:bg-neutral-800"
            imageSrc={presta}
            buttonAction={() => navigate("/prestations")}
            buttonText="Trouver un prestataire"
            highlightText=""
            Htitle="h2"
            title="Trouver votre prestataire événementiel pour un événement réussi"
            description="Vous cherchez un prestataire événementiel à Paris pour organiser un événement mémorable ? Que ce soit pour un mariage, un séminaire d'entreprise, une soirée d'entreprise, un anniversaire ou un lancement de produit, trouvez facilement les meilleurs prestataires grâce à notre plateforme. Faites de votre événement un succès en accédant à une sélection variée de services professionnels et personnalisés qui répondent à tous types de projets événementiels : corporate, privé ou culturel."
          />
        </div>

        {/* FAQ Section */}
        <div className="animate-fade-in-up animation-delay-1400">
          <CallToFaqSection
            toggleBgColor="bg-gradient-to-r from-[#4663ac] to-pink-500"
            bg="bg-white dark:bg-neutral-800"
            faqs={RESERVATION_FAQ}
          />
        </div>

        {/* Decorative Elements */}
        <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-[#4663ac]/30 to-pink-300/30 rounded-full blur-xl"></div>
        <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-pink-200/20 to-[#4663ac]/20 rounded-full blur-xl"></div>
      </div>
    </>
  );
};

export default HowItWorks;
